// Table model shared by every step:
//   { columns: [{ name, type?, letter?, header? }], rows: [{ [name]: value }] }
// Columns are addressed by name. `letter`/`header` are kept on source tables
// so the UI and AI can show "Source 1 › C · Amount".

export const columnLetter = (index) => {
  let letter = "";
  let n = index + 1;
  while (n > 0) {
    const rem = (n - 1) % 26;
    letter = String.fromCharCode(65 + rem) + letter;
    n = Math.floor((n - 1) / 26);
  }
  return letter;
};

export const letterToIndex = (letter) => {
  let index = 0;
  for (const char of String(letter).toUpperCase()) {
    const code = char.charCodeAt(0);
    if (code < 65 || code > 90) return -1;
    index = index * 26 + (code - 64);
  }
  return index - 1;
};

export const createTable = (columns = [], rows = []) => ({
  columns: columns.map((column) => (typeof column === "string" ? { name: column } : column)),
  rows,
});

export const columnNames = (table) => table.columns.map((column) => column.name);

export const hasColumn = (table, name) => table.columns.some((column) => column.name === name);

export const getColumn = (table, name) => table.columns.find((column) => column.name === name);

export const columnType = (table, name) => getColumn(table, name)?.type;

// Returns `name` or `name 2`, `name 3`, … so it doesn't clash with `taken`.
export const uniqueName = (name, taken) => {
  const used = taken instanceof Set ? taken : new Set(taken);
  const base = String(name ?? "").trim() || "Column";
  if (!used.has(base)) return base;
  let suffix = 2;
  while (used.has(`${base} ${suffix}`)) suffix++;
  return `${base} ${suffix}`;
};

// Adds or replaces a column definition; new columns go at the end (or right
// after `after` when given).
export const upsertColumn = (columns, column, { after } = {}) => {
  const existing = columns.findIndex((c) => c.name === column.name);
  if (existing !== -1) {
    const next = [...columns];
    next[existing] = { ...columns[existing], ...column };
    return next;
  }
  const anchor = after ? columns.findIndex((c) => c.name === after) : -1;
  if (anchor === -1) return [...columns, column];
  return [...columns.slice(0, anchor + 1), column, ...columns.slice(anchor + 1)];
};

// Strips source-only metadata once a column has been produced by a step.
export const derivedColumn = (name, type) => (type ? { name, type } : { name });
